import {
  defineExperiment,
  type CustomExperimentSpec,
  type PanelRow,
} from '@learnforge/demo-render/harness';
import { Fraction } from '@learnforge/demo-math/numeric';
import type { SetsContext, SetsKey } from '../types';
import { classifyNumber, whyNotIn, parseNumber, type ParsedNumber, type NumberSet } from '../sets';
import { createNumberNesting, type NumberNestingHandle } from '../render/number-nesting';
import { createComplexPlane, type ComplexPlaneHandle } from '../render/complex-plane';

// ── State ───────────────────────────────────────────────────────────────────

export interface State {
  selected: ParsedNumber | null;
  palette: ParsedNumber[];
  input: string;
  advanced: boolean;
}

// ── Default palette ──────────────────────────────────────────────────────────

/**
 * A varied palette of ParsedNumbers that demonstrates each number set.
 * Order: natural, negative integer, rational, √2 (irrational), π, 2+3i (complex).
 */
const DEFAULT_PALETTE: ParsedNumber[] = [
  { kind: 'integer', value: 5 },
  { kind: 'integer', value: -3 },
  { kind: 'rational', fraction: new Fraction(1, 2) },
  { kind: 'irrational', approx: Math.SQRT2, label: '√2' },
  { kind: 'irrational', approx: Math.PI, label: 'π' },
  { kind: 'complex', re: 2, im: 3 },
];

// ── Pure helper (exported for unit tests) ────────────────────────────────────

/**
 * Returns the `{ re, im }` coordinates for a `ParsedNumber` on the complex plane.
 *
 * - integer / rational → `{ re: numeric value, im: 0 }`
 * - irrational → `{ re: approx, im: 0 }`
 * - complex → `{ re, im }`
 */
export function pointForNumber(v: ParsedNumber): { re: number; im: number } {
  switch (v.kind) {
    case 'integer':
      return { re: v.value, im: 0 };
    case 'rational':
      return { re: v.fraction.toNumber(), im: 0 };
    case 'irrational':
      return { re: v.approx, im: 0 };
    case 'complex':
      return { re: v.re, im: v.im };
  }
}

/** Returns a short display label for a ParsedNumber. */
function labelForNumber(v: ParsedNumber): string {
  switch (v.kind) {
    case 'integer':
      return String(v.value);
    case 'rational':
      return `${v.fraction.num}/${v.fraction.den}`;
    case 'irrational':
      return v.label;
    case 'complex':
      return v.im > 0 ? `${v.re}+${v.im}i` : `${v.re}${v.im}i`;
  }
}

// ── Containment chain readout ────────────────────────────────────────────────

/** The full chain in order. */
const CHAIN: NumberSet[] = ['N', 'Z', 'Q', 'R', 'C'];
const CHAIN_GLYPHS: Record<NumberSet, string> = { N: 'ℕ', Z: 'ℤ', Q: 'ℚ', R: 'ℝ', C: 'ℂ' };

// ── Spec ────────────────────────────────────────────────────────────────────

function buildSpec(): CustomExperimentSpec<State, SetsKey, SetsContext> {
  return {
    labelKey: 'experiment.number-systems.label',
    demoId: 'sets',
    advancedKey: 'number-systems',

    initialState(ctx): State {
      return {
        palette: DEFAULT_PALETTE,
        selected: DEFAULT_PALETTE[0] ?? null,
        input: '',
        advanced: ctx.advanced,
      };
    },

    panel(_state, _ctx): PanelRow<SetsKey, State>[] {
      const rows: PanelRow<SetsKey, State>[] = [
        { kind: 'advanced-toggle', labelKey: 'btn.advanced' },
      ];
      return rows;
    },

    createRender(ctx, host, controls) {
      // ── Root container ──
      const container = document.createElement('div');
      container.className = 'ns-container';
      host.appendChild(container);

      // ── Left column: nesting diagram ──
      const leftCol = document.createElement('div');
      leftCol.className = 'ns-left-col';
      container.appendChild(leftCol);

      const nestingSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      nestingSvg.setAttribute('viewBox', '0 0 400 300');
      nestingSvg.setAttribute('class', 'ns-nesting-svg');
      leftCol.appendChild(nestingSvg);

      const nestingHandle: NumberNestingHandle = createNumberNesting(nestingSvg, {});

      // ── Right column: palette + caption + (advanced) plane ──
      const rightCol = document.createElement('div');
      rightCol.className = 'ns-right-col';
      container.appendChild(rightCol);

      // Palette chips
      const paletteEl = document.createElement('div');
      paletteEl.className = 'ns-palette';
      rightCol.appendChild(paletteEl);

      // Caption (whyNotIn explanation)
      const captionEl = document.createElement('div');
      captionEl.className = 'ns-caption';
      rightCol.appendChild(captionEl);

      // Extension chain readout (advanced)
      const chainEl = document.createElement('div');
      chainEl.className = 'ns-chain';
      rightCol.appendChild(chainEl);

      // Q-density note (advanced)
      const densityEl = document.createElement('div');
      densityEl.className = 'ns-density-note';
      rightCol.appendChild(densityEl);

      // Free-text input (advanced)
      const inputWrap = document.createElement('div');
      inputWrap.className = 'ns-input-wrap';
      const inputLabel = document.createElement('label');
      inputLabel.className = 'ns-input-label';
      const inputEl = document.createElement('input');
      inputEl.type = 'text';
      inputEl.className = 'ns-input';
      inputEl.placeholder = '√2, 1/2, 2+3i, …';
      inputWrap.appendChild(inputLabel);
      inputWrap.appendChild(inputEl);
      rightCol.appendChild(inputWrap);

      // Complex plane (advanced)
      const planeSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      planeSvg.setAttribute('viewBox', '0 0 400 300');
      planeSvg.setAttribute('class', 'ns-plane-svg');
      rightCol.appendChild(planeSvg);

      const planeHandle: ComplexPlaneHandle = createComplexPlane(planeSvg, {});

      // Track listeners for cleanup
      const abortCtrl = new AbortController();
      const { signal } = abortCtrl;

      inputEl.addEventListener(
        'input',
        () => {
          const parsed = parseNumber(inputEl.value);
          if (parsed !== null) {
            controls.setState((prev) => ({ ...prev, input: inputEl.value, selected: parsed }));
          } else {
            controls.setState((prev) => ({ ...prev, input: inputEl.value }));
          }
        },
        { signal },
      );

      function update(state: State): void {
        const advanced = ctx.advanced;
        // ── Nesting diagram ──
        const highlight = state.selected != null ? classifyNumber(state.selected) : null;
        nestingHandle.update({ highlight });

        // ── Palette chips ──
        paletteEl.replaceChildren();
        for (const v of state.palette) {
          const chip = document.createElement('button');
          chip.className = 'ns-chip';
          chip.textContent = labelForNumber(v);
          const isSelected =
            state.selected !== null && JSON.stringify(v) === JSON.stringify(state.selected);
          if (isSelected) chip.classList.add('is-selected');
          chip.addEventListener(
            'click',
            () => controls.setState((prev) => ({ ...prev, selected: v, input: '' })),
            { signal },
          );
          paletteEl.appendChild(chip);
        }

        // ── Caption ──
        captionEl.textContent = '';
        if (state.selected !== null) {
          const set = classifyNumber(state.selected);
          const setGlyph = CHAIN_GLYPHS[set];
          const caption = document.createElement('span');
          caption.className = 'ns-caption-text';
          caption.textContent = `${labelForNumber(state.selected)} ∈ ${setGlyph}`;
          captionEl.appendChild(caption);

          // Show the boundary reason: the largest set in the chain that does
          // NOT contain this number (i.e. the set just below the number's
          // actual classification).  Iterate the chain in reverse (largest →
          // smallest) and take the first set that still excludes the number.
          for (let i = CHAIN.length - 1; i >= 0; i--) {
            const s = CHAIN[i]!;
            if (s === set) continue; // skip the number's own classification
            const reason = whyNotIn(s, state.selected);
            if (reason !== null) {
              const detail = document.createElement('span');
              detail.className = 'ns-caption-detail';
              detail.textContent = ` — ${ctx.i18n.t(reason)}`;
              captionEl.appendChild(detail);
              break;
            }
          }
        }

        // ── Advanced features ──
        chainEl.style.display = advanced ? '' : 'none';
        densityEl.style.display = advanced ? '' : 'none';
        inputWrap.style.display = advanced ? '' : 'none';
        planeSvg.style.display = advanced ? '' : 'none';

        if (advanced) {
          inputLabel.textContent = ctx.i18n.t('param.free-input');

          // Sync the input field only if it differs (avoid re-triggering event)
          if (inputEl.value !== state.input) {
            inputEl.value = state.input;
          }

          // Extension chain readout
          chainEl.replaceChildren();
          const chainLabel = document.createElement('span');
          chainLabel.className = 'ns-chain-label';
          chainLabel.textContent = `${ctx.i18n.t('label.extension-chain')}: `;
          chainEl.appendChild(chainLabel);

          const chainRow = document.createElement('span');
          chainRow.className = 'ns-chain-row';
          const selectedSet = state.selected != null ? classifyNumber(state.selected) : null;
          for (let i = 0; i < CHAIN.length; i++) {
            const s = CHAIN[i]!;
            const glyph = CHAIN_GLYPHS[s];
            const span = document.createElement('span');
            span.className = 'ns-chain-step';
            if (selectedSet === s) span.classList.add('is-selected');
            span.textContent = glyph;
            chainRow.appendChild(span);
            if (i < CHAIN.length - 1) {
              const arrow = document.createElement('span');
              arrow.className = 'ns-chain-arrow';
              arrow.textContent = '⊂';
              chainRow.appendChild(arrow);
            }
          }
          chainEl.appendChild(chainRow);

          // Q-density note
          densityEl.textContent = ctx.i18n.t('note.q-density');

          // Complex plane
          if (state.selected !== null) {
            planeHandle.update({ point: pointForNumber(state.selected) });
          } else {
            planeHandle.update({ point: null });
          }
        }
      }

      controls.registerCleanup(() => {
        abortCtrl.abort();
        nestingHandle.destroy();
        planeHandle.destroy();
      });

      return {
        update,
        dispose() {
          /* cleanup via registerCleanup */
        },
      };
    },

    applyShotState(state): State {
      // Shot pose: √2 selected (irrational → lights ℝ and ℂ)
      const sqrt2: ParsedNumber = { kind: 'irrational', approx: Math.SQRT2, label: '√2' };
      return {
        ...state,
        selected: sqrt2,
        input: '',
        advanced: false,
      };
    },

    actions(_state, ctx) {
      return {
        onReset(): State {
          return {
            palette: DEFAULT_PALETTE,
            selected: DEFAULT_PALETTE[0] ?? null,
            input: '',
            advanced: ctx.advanced,
          };
        },
        onRandomise(s: State): State {
          // Cycle through the palette using the current index
          const idx = s.palette.findIndex((v) => JSON.stringify(v) === JSON.stringify(s.selected));
          const next = (idx + 1) % s.palette.length;
          return { ...s, selected: s.palette[next] ?? null, input: '' };
        },
      };
    },
  };
}

export const experiment = defineExperiment(buildSpec());
